import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './components/ui/toast';
import { AuthProvider } from './lib/auth';
import { RequireAuth } from './components/auth/require-auth';
import { RequireAdmin } from './components/auth/require-admin';
import { Login } from './screens/login';
import { ForgotPassword } from './screens/forgot-password';
import { ResetPassword } from './screens/reset-password';
import { NotFound } from './screens/not-found';
import { ChangePassword } from './screens/change-password';
import { Dashboard } from './screens/dashboard';
import { Campaigns } from './screens/campaigns';
import { CampaignForm } from './screens/campaign-form';
import { CampaignDuplicate } from './screens/campaign-duplicate';
import { Advertisers } from './screens/advertisers';
import { Publishers } from './screens/publishers';
import { Suppression } from './screens/suppression';
import { Sites } from './screens/sites';
import { PromoTypes } from './screens/promo-types';
import { CtoLinks } from './screens/cto-links';
import { PageOrder } from './screens/page-order';
import { AllowList } from './screens/allow-list';
import { CtoApprovedDomains } from './screens/cto-approved-domains';
import { Leads } from './screens/leads';
import { LeadDetail } from './screens/lead-detail';
import { TestRuns } from './screens/test-runs';
import { PartnerCredentials } from './screens/partner-credentials';
import { PartnerEndpoints } from './screens/partner-endpoints';
import { RouteRules } from './screens/route-rules';
import { CoopCounts } from './screens/reports/co-op-counts';
import { CtoStats } from './screens/reports/cto-stats';
import { CtoCommon } from './screens/reports/cto-common';
import { CtoPaidSocial } from './screens/reports/cto-paid-social';
import { CustomOptinStats } from './screens/reports/custom-optin-stats';
import { PaidSocialStats } from './screens/reports/paid-social-stats';
import { DailySpend } from './screens/reports/daily-spend';
import { Distribution } from './screens/distribution';
import { Users } from './screens/users';

export default function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        {/* Auth above the routes (epic 076): /login and /change-password
            mount OUTSIDE the Layout; everything else stays under it. */}
        <AuthProvider>
          <Routes>
            {/* Auth screens — outside the Layout (no sidebar/header). The
                forgot/reset legs (epic 103 D4/D8) are unauthenticated: they
                sit beside /login outside RequireAuth, reachable by deep link
                (the email's /reset-password?token=… lands here on reload via
                the SPA fallback). */}
            <Route path="/login" element={<Login />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* The shell route is gated (QA round 18 #134 + #137): a loading
                frame while the mount-time session check runs, a /login
                redirect when signed out, the Layout when signed in. The `*`
                child at the end catches paths the router does not handle —
                nginx's SPA fallback serves those as index.html, so they must
                get the same gate + a defined 404 frame, not a white screen. */}
            <Route path="/" element={<RequireAuth />}>
              <Route index element={<Dashboard />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="campaigns/new" element={<CampaignForm />} />
              <Route path="campaigns/:id" element={<CampaignForm />} />
              <Route path="campaigns/:id/duplicate" element={<CampaignDuplicate />} />
              <Route path="advertisers" element={<Advertisers />} />
              <Route path="publishers" element={<Publishers />} />
              <Route path="suppression" element={<Suppression />} />
              <Route path="sites" element={<Sites />} />
              <Route path="promo-types" element={<PromoTypes />} />
              <Route path="cto-links" element={<CtoLinks />} />
              <Route path="page-order" element={<PageOrder />} />
              <Route path="allow-list" element={<AllowList />} />
              {/* Approved domains (epic 108 task 02): the CTO singleton
                  setting, admin-gated like the Users route. */}
              <Route
                path="cto-approved-domains"
                element={
                  <RequireAdmin>
                    <CtoApprovedDomains />
                  </RequireAdmin>
                }
              />
              <Route path="leads" element={<Leads />} />
              <Route path="leads/:id" element={<LeadDetail />} />
              <Route path="test-runs/:campaignId?" element={<TestRuns />} />
              <Route path="advertiser-credentials" element={<PartnerCredentials />} />
              <Route path="advertiser-endpoints" element={<PartnerEndpoints />} />
              <Route path="route-rules" element={<RouteRules />} />
              <Route path="reports" element={<Navigate to="/reports/co-op" replace />} />
              <Route path="reports/co-op" element={<CoopCounts />} />
              <Route path="reports/custom-optin" element={<CustomOptinStats />} />
              <Route path="reports/cto" element={<CtoStats />} />
              <Route path="reports/cto-common" element={<CtoCommon />} />
              <Route path="reports/paid-social" element={<PaidSocialStats />} />
              <Route path="reports/cto-paid-social" element={<CtoPaidSocial />} />
              <Route path="reports/daily-spend" element={<DailySpend />} />
              <Route path="distribution" element={<Distribution />} />
              {/* User management (epic 103 D8): route-level double gate —
                  session (RequireAuth above) + role (RequireAdmin). */}
              <Route
                path="users"
                element={
                  <RequireAdmin>
                    <Users />
                  </RequireAdmin>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ToastProvider>
  );
}
